import { get, post, put, del } from '@aws-amplify/api';
import { 
  Email, 
  EmailInput, 
  ProcessedEmail, 
  Classification, 
  Summary, 
  Deadline, 
  FollowUp, 
  Notification, 
  NotificationPreferences, 
  User, 
  UserInput,
  ProcessedAttachment 
} from '../types';

const API_NAME = 'outlook-assistant-api';

export interface ApiResponse<T> {
  data?: T;
  error?: {
    message: string;
    details?: any;
  };
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  hasMore: boolean;
}

class ApiClient {
  private async makeRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    requestData?: any,
    queryParams?: Record<string, any>
  ): Promise<ApiResponse<T>> {
    try {
      const init: any = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      if (requestData) {
        init.body = requestData;
      }

      if (queryParams) {
        init.queryStringParameters = queryParams;
      }

      let response;
      switch (method) {
        case 'GET':
          response = await get({ apiName: API_NAME, path, options: init }).response;
          break;
        case 'POST':
          response = await post({ apiName: API_NAME, path, options: init }).response;
          break;
        case 'PUT':
          response = await put({ apiName: API_NAME, path, options: init }).response;
          break;
        case 'DELETE':
          response = await del({ apiName: API_NAME, path, options: init }).response;
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }
      
      const responseData = await response.body.json() as T;

      return { data: responseData };
    } catch (error: any) {
      console.error(`API ${method} ${path} error:`, error);
      
      // Enhanced error handling with more specific error types
      let errorMessage = 'An error occurred';
      let errorDetails = error;
      
      if (error.name === 'NetworkError' || error.message?.includes('Network')) {
        errorMessage = 'Network connection failed - API may be offline';
      } else if (error.name === 'AuthError' || error.message?.includes('auth')) {
        errorMessage = 'Authentication failed - please sign in again';
      } else if (error.response?.status === 404) {
        errorMessage = 'API endpoint not found';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error - please try again later';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        error: {
          message: errorMessage,
          details: errorDetails,
        },
      };
    }
  }

  // Email endpoints
  async getEmails(params?: {
    limit?: number;
    offset?: number;
    classification?: 'actionable' | 'non-actionable';
  }): Promise<ApiResponse<PaginatedResponse<Email>>> {
    return this.makeRequest('GET', '/api/v1/emails', undefined, params);
  }

  async processEmail(email: EmailInput): Promise<ApiResponse<ProcessedEmail>> {
    return this.makeRequest('POST', '/api/v1/emails', email);
  }

  async getEmail(emailId: string): Promise<ApiResponse<Email>> {
    return this.makeRequest('GET', `/api/v1/emails/${emailId}`);
  }

  async updateEmail(
    emailId: string, 
    action: 'reprocess' | 'mark_read' | 'mark_unread' | 'archive'
  ): Promise<ApiResponse<Email>> {
    return this.makeRequest('POST', `/api/v1/emails/${emailId}`, { action });
  }

  // Classification endpoints
  async classifyContent(content: string, subject?: string): Promise<ApiResponse<Classification>> {
    return this.makeRequest('POST', '/api/v1/classify', { content, subject });
  }

  async classifyEmail(emailId: string): Promise<ApiResponse<Classification>> {
    return this.makeRequest('POST', `/api/v1/classify/${emailId}`);
  }

  // Summarization endpoints
  async summarizeContent(content: string, maxLength?: number): Promise<ApiResponse<Summary>> {
    return this.makeRequest('POST', '/api/v1/summarize', { content, maxLength });
  }

  async summarizeEmail(emailId: string, maxLength?: number): Promise<ApiResponse<Summary>> {
    return this.makeRequest('POST', `/api/v1/summarize/${emailId}`, { maxLength });
  }

  // Attachment endpoints
  async processAttachment(file: File, emailId?: string): Promise<ApiResponse<ProcessedAttachment>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (emailId) {
        formData.append('emailId', emailId);
      }

      const init = {
        headers: {},
        body: formData,
      };

      const response = await post({ 
        apiName: API_NAME, 
        path: '/api/v1/attachments', 
        options: init 
      }).response;
      const data = await response.body.json() as unknown as ProcessedAttachment;
      return { data };
    } catch (error: any) {
      console.error('Attachment processing error:', error);
      return {
        error: {
          message: error.response?.data?.message || error.message || 'Attachment processing failed',
          details: error.response?.data?.details || error.response?.data,
        },
      };
    }
  }

  async getAttachment(attachmentId: string): Promise<ApiResponse<ProcessedAttachment>> {
    return this.makeRequest('GET', `/api/v1/attachments/${attachmentId}`);
  }

  // Deadline endpoints
  async getDeadlines(params?: {
    upcoming?: boolean;
    days?: number;
  }): Promise<ApiResponse<{ deadlines: Deadline[] }>> {
    return this.makeRequest('GET', '/api/v1/deadlines', undefined, params);
  }

  async extractDeadlines(emailId: string, content: string): Promise<ApiResponse<{ deadlines: Deadline[] }>> {
    return this.makeRequest('POST', '/api/v1/deadlines', { emailId, content });
  }

  async getDeadlineReminders(): Promise<ApiResponse<{ reminders: any[] }>> {
    return this.makeRequest('GET', '/api/v1/deadlines/reminders');
  }

  async createDeadlineReminder(
    deadlineId: string, 
    reminderTime: string, 
    message?: string
  ): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/deadlines/reminders', {
      deadlineId,
      reminderTime,
      message,
    });
  }

  // Follow-up endpoints
  async getFollowUps(status?: 'pending' | 'reminded' | 'resolved'): Promise<ApiResponse<{ followups: FollowUp[] }>> {
    const params = status ? { status } : undefined;
    return this.makeRequest('GET', '/api/v1/followups', undefined, params);
  }

  async detectFollowUps(emailId: string): Promise<ApiResponse<{ followups: FollowUp[] }>> {
    return this.makeRequest('POST', '/api/v1/followups', { emailId });
  }

  async getFollowUpReminders(): Promise<ApiResponse<{ reminders: any[] }>> {
    return this.makeRequest('GET', '/api/v1/followups/reminders');
  }

  async createFollowUpReminder(followUpId: string): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/followups/reminders', { followUpId });
  }

  async getFollowUpSuggestions(): Promise<ApiResponse<{ suggestions: any[] }>> {
    return this.makeRequest('GET', '/api/v1/followups/suggestions');
  }

  async createFollowUpSuggestion(emailId: string): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/followups/suggestions', { emailId });
  }

  async getExternalResponses(): Promise<ApiResponse<{ responses: any[] }>> {
    return this.makeRequest('GET', '/api/v1/followups/external-responses');
  }

  async monitorExternalResponse(emailId: string): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/followups/external-responses', { emailId });
  }

  // Notification endpoints
  async getNotifications(unread?: boolean): Promise<ApiResponse<{ notifications: Notification[] }>> {
    const params = unread !== undefined ? { unread } : undefined;
    return this.makeRequest('GET', '/api/v1/notifications', undefined, params);
  }

  async sendNotification(notification: {
    type: 'deadline' | 'followup' | 'classification' | 'summary';
    title: string;
    message: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  }): Promise<ApiResponse<Notification>> {
    return this.makeRequest('POST', '/api/v1/notifications', notification);
  }

  async getNotificationPreferences(): Promise<ApiResponse<NotificationPreferences>> {
    return this.makeRequest('GET', '/api/v1/notifications/preferences');
  }

  async updateNotificationPreferences(preferences: NotificationPreferences): Promise<ApiResponse<NotificationPreferences>> {
    return this.makeRequest('PUT', '/api/v1/notifications/preferences', preferences);
  }

  // User endpoints
  async getUserProfile(): Promise<ApiResponse<User>> {
    return this.makeRequest('GET', '/api/v1/users');
  }

  async createUserProfile(user: UserInput): Promise<ApiResponse<User>> {
    return this.makeRequest('POST', '/api/v1/users', user);
  }

  async updateUserProfile(user: UserInput): Promise<ApiResponse<User>> {
    return this.makeRequest('PUT', '/api/v1/users', user);
  }

  async storeMicrosoftTokens(tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/users/microsoft-tokens', tokens);
  }

  // ML endpoints
  async trainModel(): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/ml/training');
  }

  async getTrainingStatus(): Promise<ApiResponse<any>> {
    return this.makeRequest('GET', '/api/v1/ml/training/status');
  }

  async prepareTrainingData(): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/ml/training/prepare-data');
  }

  async classifyWithML(content: string): Promise<ApiResponse<Classification>> {
    return this.makeRequest('POST', '/api/v1/ml/inference/classify', { content });
  }

  async submitFeedback(emailId: string, feedback: {
    correctClassification: boolean;
    actualClassification?: string;
    comments?: string;
  }): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/ml/inference/feedback', {
      emailId,
      ...feedback,
    });
  }

  async createABTest(testConfig: any): Promise<ApiResponse<any>> {
    return this.makeRequest('POST', '/api/v1/ml/ab-test', testConfig);
  }

  async getABTestStatus(testId: string): Promise<ApiResponse<any>> {
    return this.makeRequest('GET', `/api/v1/ml/ab-test/status?testId=${testId}`);
  }

  // Generic HTTP methods for testing and flexibility
  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    const response = await this.makeRequest<T>('GET', path, undefined, params);
    if (ApiClient.isApiError(response)) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  async post<T = any>(path: string, data?: any): Promise<T> {
    const response = await this.makeRequest<T>('POST', path, data);
    if (ApiClient.isApiError(response)) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  async put<T = any>(path: string, data?: any): Promise<T> {
    const response = await this.makeRequest<T>('PUT', path, data);
    if (ApiClient.isApiError(response)) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  async delete<T = any>(path: string): Promise<T> {
    const response = await this.makeRequest<T>('DELETE', path);
    if (ApiClient.isApiError(response)) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  // Utility methods
  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    try {
      // First try a simple connectivity test
      const response = await this.makeRequest<{ status: string; timestamp: string }>('GET', '/health');
      if (response.data) {
        return response;
      }
      
      // Fallback to the full health endpoint
      return this.makeRequest('GET', '/api/v1/health');
    } catch (error) {
      // If both fail, return a mock health response to indicate the service structure
      return {
        data: {
          status: 'demo',
          timestamp: new Date().toISOString()
        }
      };
    }
  }

  // Error handling utility
  static isApiError(response: ApiResponse<any>): response is { error: { message: string; details?: any } } {
    return 'error' in response && response.error !== undefined;
  }

  // Retry mechanism for failed requests
  async retryRequest<T>(
    requestFn: () => Promise<ApiResponse<T>>,
    maxRetries: number = 3,
    delay: number = 1000
  ): Promise<ApiResponse<T>> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const result = await requestFn();
        
        if (!ApiClient.isApiError(result)) {
          return result;
        }
        
        // Don't retry client errors (4xx)
        if (result.error.message.includes('400') || result.error.message.includes('401') || 
            result.error.message.includes('403') || result.error.message.includes('404')) {
          return result;
        }
        
        lastError = result.error;
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
        }
      } catch (error) {
        lastError = error;
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt)));
        }
      }
    }
    
    return {
      error: {
        message: `Request failed after ${maxRetries + 1} attempts`,
        details: lastError,
      },
    };
  }
}

export const apiClient = new ApiClient();
export { ApiClient };
export default apiClient;